package com.arena_web.ui.theme

import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.init.InitSilkContext
import com.varabyte.kobweb.silk.init.registerStyleBase
import kotlinx.browser.document
import org.jetbrains.compose.web.css.Color as CssColor
import org.jetbrains.compose.web.css.px

fun initAppTheme(ctx: InitSilkContext) {

    // Load Urbanist + IBM Plex Sans from Google Fonts
    val googleFontsLink = document.createElement("link") as org.w3c.dom.HTMLLinkElement
    googleFontsLink.rel = "stylesheet"
    googleFontsLink.href = "https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;600&family=Urbanist:wght@300;400;500;600;700&display=swap"
    document.head?.appendChild(googleFontsLink)

    // Preconnect for faster font loading
    val preconnect = document.createElement("link") as org.w3c.dom.HTMLLinkElement
    preconnect.rel = "preconnect"
    preconnect.href = "https://fonts.googleapis.com"
    document.head?.insertBefore(preconnect, document.head?.firstChild)

    val preconnectGstatic = document.createElement("link") as org.w3c.dom.HTMLLinkElement
    preconnectGstatic.rel = "preconnect"
    preconnectGstatic.href = "https://fonts.gstatic.com"
    preconnectGstatic.setAttribute("crossorigin", "")
    document.head?.insertBefore(preconnectGstatic, document.head?.firstChild)

    // Global keyframe animations
    val styleEl = document.createElement("style")
    styleEl.textContent = """
        @keyframes arena-border-pulse {
            0%, 100% { border-color: rgba(0, 97, 255, 0.3); }
            50% { border-color: rgba(0, 97, 255, 0.8); }
        }
    """.trimIndent()
    document.head?.appendChild(styleEl)

    // Global responsive scale factor: 1 at 1920×1080, scales proportionally in both directions
    ctx.stylesheet.registerStyleBase(":root") {
        Modifier.styleModifier {
            property("--scale", "max(0.4px, min(calc(100vw / 1920), calc(100vh / 1080)))")
            // Mobile scale factor — 1 at the 390-px design baseline, scales
            // DOWN proportionally on narrower phones (e.g. 0.82 on a 320-px
            // device). Caps at 1 so wider mobile viewports don't blow up the
            // design. Used by raw-px mobile components via `m()` helpers.
            property("--mobile-scale", "min(1px, calc(100vw / 390))")
        }
    }

    // SVG noise texture to prevent color banding on near-black #02040B background
    val noiseSvg = buildString {
        append("<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'>")
        append("<filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/></filter>")
        append("<rect width='100%' height='100%' filter='url(%23n)' opacity='0.03'/></svg>")
    }
    val noiseDataUri = "url(\"data:image/svg+xml,${noiseSvg}\")"

    // ── Theme design tokens ─────────────────────────────────────────────
    // :root carries the dark (default) values — these mirror the hardcoded
    // colors used across the app today, bit-for-bit. [data-theme=light]
    // overrides them with the light-glassmorphism palette (spec:
    // docs/superpowers/specs/2026-09-22-light-theme-design.md). Components
    // migrate to var(--arena-*) via ThemeVars; ThemeController flips the
    // attribute on <html>.
    ctx.stylesheet.registerStyleBase(":root") {
        Modifier.styleModifier {
            property("--arena-bg", "#02040B")
            property(
                "--arena-bg-image",
                "$noiseDataUri, " +
                    "linear-gradient(0deg, rgba(60, 121, 241, 0) 0%, " +
                    "rgba(60, 121, 241, 0.072) 64%, rgba(60, 121, 241, 0.072) 100%)"
            )
            property("--arena-bg-repeat", "repeat, no-repeat")
            property("--arena-glass", "rgba(255, 255, 255, 0.04)")
            property("--arena-glass-strong", "rgba(255, 255, 255, 0.08)")
            property("--arena-surface", "#0F172A")
            property("--arena-surface-variant", "#112140")
            property("--arena-surface-popup", "#060E1E")
            property("--arena-text-1", "#B4CCF6")
            property("--arena-text-2", "#BED6F5")
            property("--arena-text-3", "#959697")
            property("--arena-text-muted", "#99B1BF")
            property("--arena-text-nav-inactive", "#CCCDCE")
            property("--arena-border-top", "rgba(255, 255, 255, 0.4)")
            property("--arena-border-bottom", "rgba(255, 255, 255, 0.12)")
            property("--arena-border", "#282D3C")
            property("--arena-shadow-card", "0 8px 24px rgba(0, 0, 0, 0.4)")
            property("--arena-glow-cta", "none")
            property("--arena-overlay", "rgba(0, 0, 0, 0.7)")
            property("--arena-placeholder", "rgba(153, 177, 191, 0.5)")
            property("--arena-map-bg", "#0e1626")
            property("--arena-icon-filter", AppColors.IconDefaultFilter)
            // Gradient tokens — dark values are the exact strings AppGradients
            // shipped with (see Colors.kt history); AppGradients members now
            // resolve through these vars.
            property("--arena-grad-surface-01", "linear-gradient(55deg, rgba(60, 121, 241, 0.45), rgba(5, 4, 12, 0.10))")
            property("--arena-grad-surface-02", "linear-gradient(0deg, rgba(60, 121, 241, 0.45), rgba(5, 4, 12, 0.10))")
            property("--arena-grad-surface-inner-l", "linear-gradient(-115deg, rgba(0, 59, 174, 0.20), rgba(3, 87, 230, 0.20))")
            property("--arena-grad-interactive-secondary", "linear-gradient(105deg, #003BAE24, #0357E624)")
            property("--arena-grad-sport-circle-fill", "linear-gradient(105deg, rgba(0, 59, 174, 0.08), rgba(3, 87, 230, 0.08))")
            property("--arena-grad-sport-circle-border", "linear-gradient(180deg, rgba(255, 255, 255, 0.60), rgba(206, 207, 215, 0.10))")
            property("--arena-grad-sport-dropdown-border", "linear-gradient(180deg, rgba(60, 121, 241, 0.133) 49%, rgba(5, 4, 12, 0.031) 95%)")
            property(
                "--arena-grad-panel-divider",
                "linear-gradient(90deg, rgba(148, 149, 152, 0) 0%, rgba(148, 149, 152, 0.27) 25%, " +
                    "rgba(98, 99, 101, 0.30) 50%, rgba(148, 149, 152, 0.27) 75%, rgba(148, 149, 152, 0) 100%)"
            )
            property("--arena-grad-score-toggle", "linear-gradient(180deg, rgba(12, 66, 255, 0.70), rgba(102, 102, 102, 0))")
            property(
                "--arena-grad-overlay-edge",
                "linear-gradient(90deg, rgba(2, 4, 11, 1) 0%, rgba(2, 4, 11, 1) 14%, rgba(2, 4, 11, 0.80) 28%, " +
                    "rgba(2, 4, 11, 0.60) 42%, rgba(2, 4, 11, 0.30) 57%, rgba(2, 4, 11, 0.15) 71%, rgba(2, 4, 11, 0) 100%)"
            )
            property(
                "--arena-grad-overlay-players",
                "linear-gradient(to top, rgba(2, 4, 11, 1) 0%, rgba(0, 0, 0, 0.60) 33%, " +
                    "rgba(0, 0, 0, 0.30) 66%, rgba(0, 0, 0, 0) 100%)"
            )
            property(
                "--arena-grad-overlay-partial",
                "linear-gradient(90deg, rgba(0, 0, 0, 0.80) 10%, rgba(0, 0, 0, 0.60) 30%, " +
                    "rgba(0, 0, 0, 0.40) 50%, rgba(0, 0, 0, 0.20) 70%, rgba(153, 153, 153, 0) 91.4%)"
            )
            property(
                "--arena-grad-overlay-players-h",
                "linear-gradient(90deg, rgba(2, 4, 11, 1) 0%, rgba(2, 4, 11, 0.80) 25%, " +
                    "rgba(2, 4, 11, 0.40) 55%, rgba(2, 4, 11, 0) 100%)"
            )
            property("--arena-grad-border-01", "linear-gradient(-88deg, rgba(46, 96, 217, 1) 59%, rgba(109, 168, 255, 0.10) 95%)")
            property("--arena-news-card-border", "rgba(76, 124, 230, 0.45)")
            property("--arena-grad-border-inner-l", "linear-gradient(-84deg, rgba(41, 52, 138, 0.20), rgba(3, 87, 230, 0.20))")
            property("--arena-grad-btn-border", "linear-gradient(180deg, rgba(255, 255, 255, 0.4), rgba(255, 255, 255, 0.12))")
            property("--arena-grad-panel-border", "linear-gradient(180deg, rgba(255, 255, 255, 0.4), rgba(255, 255, 255, 0.12))")
            property("--arena-grad-sheet-border", "linear-gradient(180deg, #1D3358, #05040C1A)")
        }
    }
    ctx.stylesheet.registerStyleBase("[data-theme=\"light\"]") {
        Modifier.styleModifier {
            property("--arena-bg", "#F7FAFF")
            property(
                "--arena-bg-image",
                "$noiseDataUri, " +
                    "radial-gradient(420px 300px at 85% -5%, rgba(0, 229, 164, 0.10), transparent 70%), " +
                    "radial-gradient(500px 380px at -10% 12%, rgba(0, 97, 255, 0.12), transparent 70%), " +
                    "radial-gradient(600px 500px at 50% 115%, rgba(0, 97, 255, 0.14), transparent 60%), " +
                    "linear-gradient(180deg, #EDF2FB 0%, #F7FAFF 45%, #EAF1FC 100%)"
            )
            property("--arena-bg-repeat", "repeat, no-repeat, no-repeat, no-repeat, no-repeat")
            property("--arena-glass", "rgba(255, 255, 255, 0.72)")
            property("--arena-glass-strong", "rgba(255, 255, 255, 0.85)")
            property("--arena-surface", "#FFFFFF")
            property("--arena-surface-variant", "#EEF3FB")
            property("--arena-surface-popup", "#FFFFFF")
            property("--arena-text-1", "#101B33")
            property("--arena-text-2", "#33415E")
            property("--arena-text-3", "#5A6B8C")
            property("--arena-text-muted", "#7A8AA8")
            property("--arena-text-nav-inactive", "#8A97B0")
            property("--arena-border-top", "rgba(140, 165, 215, 0.55)")
            property("--arena-border-bottom", "rgba(163, 180, 214, 0.55)")
            property("--arena-border", "#DCE4F2")
            property("--arena-shadow-card", "0 8px 24px rgba(23, 43, 99, 0.10)")
            property("--arena-glow-cta", "0 10px 26px rgba(0, 97, 255, 0.38)")
            property("--arena-overlay", "rgba(23, 43, 99, 0.35)")
            property("--arena-placeholder", "rgba(90, 107, 140, 0.55)")
            property("--arena-map-bg", "#E7EEF9")
            // white SVG → #33415E (slate navy) for light surfaces
            property(
                "--arena-icon-filter",
                "brightness(0) invert(24%) sepia(12%) saturate(1400%) hue-rotate(190deg) brightness(95%)"
            )
            // Gradient tokens — light-glassmorphism counterparts. Overlay
            // scrims fade to the light page background (247,250,255) instead
            // of near-black so photos melt into the light canvas.
            property("--arena-grad-surface-01", "linear-gradient(55deg, rgba(255, 255, 255, 0.75), rgba(238, 243, 251, 0.35))")
            property("--arena-grad-surface-02", "linear-gradient(0deg, rgba(255, 255, 255, 0.75), rgba(238, 243, 251, 0.35))")
            property("--arena-grad-surface-inner-l", "linear-gradient(-115deg, rgba(0, 97, 255, 0.06), rgba(3, 87, 230, 0.06))")
            property("--arena-grad-interactive-secondary", "linear-gradient(105deg, rgba(0, 59, 174, 0.13), rgba(3, 87, 230, 0.13))")
            // Icon circle matches the header notification buttons: same blue
            // tint over the same darker pill base.
            property(
                "--arena-grad-sport-circle-fill",
                "linear-gradient(105deg, rgba(0, 59, 174, 0.13), rgba(3, 87, 230, 0.13)), " +
                    "linear-gradient(0deg, #E2EAF6, #E2EAF6)"
            )
            property("--arena-grad-sport-circle-border", "linear-gradient(180deg, rgba(140, 165, 215, 0.60), rgba(140, 165, 215, 0.25))")
            property("--arena-grad-sport-dropdown-border", "linear-gradient(180deg, rgba(60, 121, 241, 0.20) 49%, rgba(163, 180, 214, 0.10) 95%)")
            property(
                "--arena-grad-panel-divider",
                "linear-gradient(90deg, rgba(122, 138, 168, 0) 0%, rgba(122, 138, 168, 0.27) 25%, " +
                    "rgba(122, 138, 168, 0.35) 50%, rgba(122, 138, 168, 0.27) 75%, rgba(122, 138, 168, 0) 100%)"
            )
            property("--arena-grad-score-toggle", "linear-gradient(180deg, rgba(12, 66, 255, 0.55), rgba(255, 255, 255, 0))")
            property(
                "--arena-grad-overlay-edge",
                "linear-gradient(90deg, rgba(247, 250, 255, 1) 0%, rgba(247, 250, 255, 1) 14%, rgba(247, 250, 255, 0.80) 28%, " +
                    "rgba(247, 250, 255, 0.60) 42%, rgba(247, 250, 255, 0.30) 57%, rgba(247, 250, 255, 0.15) 71%, rgba(247, 250, 255, 0) 100%)"
            )
            property(
                "--arena-grad-overlay-players",
                "linear-gradient(to top, rgba(247, 250, 255, 1) 0%, rgba(247, 250, 255, 0.60) 33%, " +
                    "rgba(247, 250, 255, 0.30) 66%, rgba(247, 250, 255, 0) 100%)"
            )
            property(
                "--arena-grad-overlay-partial",
                "linear-gradient(90deg, rgba(247, 250, 255, 0.80) 10%, rgba(247, 250, 255, 0.60) 30%, " +
                    "rgba(247, 250, 255, 0.40) 50%, rgba(247, 250, 255, 0.20) 70%, rgba(247, 250, 255, 0) 91.4%)"
            )
            property(
                "--arena-grad-overlay-players-h",
                "linear-gradient(90deg, rgba(247, 250, 255, 1) 0%, rgba(247, 250, 255, 0.80) 25%, " +
                    "rgba(247, 250, 255, 0.40) 55%, rgba(247, 250, 255, 0) 100%)"
            )
            property("--arena-grad-border-01", "linear-gradient(-88deg, rgba(46, 96, 217, 0.55) 59%, rgba(109, 168, 255, 0.15) 95%)")
            property("--arena-news-card-border", "rgba(76, 124, 230, 0.35)")
            property("--arena-grad-border-inner-l", "linear-gradient(-84deg, rgba(41, 52, 138, 0.10), rgba(3, 87, 230, 0.10))")
            property("--arena-grad-btn-border", "linear-gradient(180deg, rgba(140, 165, 215, 0.65), rgba(140, 165, 215, 0.28))")
            property("--arena-grad-panel-border", "linear-gradient(180deg, rgba(140, 165, 215, 0.65), rgba(140, 165, 215, 0.28))")
            property("--arena-grad-sheet-border", "linear-gradient(180deg, #C9D6EE, rgba(247, 250, 255, 0.10))")
            // ── Light-only overrides (--l-*) ─────────────────────────────
            // Convention for one-off colors that don't warrant a full token:
            // call sites write `var(--l-<name>, <original-dark-literal>)`.
            // These vars exist ONLY here, so dark mode always resolves the
            // fallback — dark stays bit-for-bit identical by construction.
            // Full-page aurora backdrop for layout Boxes that paint their own
            // opaque surface over <body> (PageLayout / MobilePageLayout).
            property(
                "--l-page-bg-image",
                "$noiseDataUri, " +
                    "radial-gradient(420px 300px at 85% -5%, rgba(0, 229, 164, 0.10), transparent 70%), " +
                    "radial-gradient(500px 380px at -10% 12%, rgba(0, 97, 255, 0.12), transparent 70%), " +
                    "radial-gradient(600px 500px at 50% 115%, rgba(0, 97, 255, 0.14), transparent 60%), " +
                    "linear-gradient(180deg, #EDF2FB 0%, #F7FAFF 45%, #EAF1FC 100%)"
            )
            property("--l-glass-base", "rgba(255, 255, 255, 0.72)")
            property("--l-nav-bg", "rgba(255, 255, 255, 0.68)")
            // Secondary solid surface (dark #09142D): dropdown/hover rows,
            // chip bases behind gradient rings.
            property("--l-surface-2", "#EEF3FB")
            // Map chrome (Leaflet zoom bar / attribution) + hairlines.
            property("--l-map-chrome", "rgba(255, 255, 255, 0.85)")
            property("--l-map-chrome-disabled", "rgba(90, 107, 140, 0.35)")
            property("--l-map-attr", "rgba(90, 107, 140, 0.75)")
            property("--l-map-attr-link", "rgba(51, 65, 94, 0.9)")
            property("--l-hairline", "rgba(23, 43, 99, 0.10)")
            property("--l-hover", "rgba(0, 97, 255, 0.07)")
            property("--l-hover-strong", "rgba(0, 97, 255, 0.12)")
            // Confirm-dialog / small-popup surface (dark #09142D navy).
            property("--l-popup", "#FFFFFF")
            // Navy surface family (chips/pill bases, dropdown panels, hovers).
            property("--l-s2", "#E9F0FA")
            property("--l-s3", "#FFFFFF")
            property("--l-hover2", "#DDE7F7")
            // Chart grid/axis hairlines (dark: faint white).
            property("--l-chart-grid", "rgba(23, 43, 99, 0.12)")
            // Vertical glass sheen gradient (dark: faint white wash).
            property("--l-glass-grad", "linear-gradient(180deg, rgba(255,255,255,0.75) 0%, rgba(255,255,255,0.45) 100%)")
            // Near-opaque wash over full-bleed hero photos (profile/arena
            // stadium backdrops) so light panels stay readable; the photo
            // ghosts through at ~8%.
            // Hero photo relight: dark surroundings flip to light while the
            // stadium keeps its hues; slight desaturate + soft-light bias.
            property("--l-hero-filter", "invert(1) hue-rotate(180deg) saturate(0.85)")
            property("--l-hero-blend", "multiply")
            // Header wordmark/label SVG art is white-gradient — darken on light.
            property("--l-logo-filter", "brightness(0.35) saturate(1.6)")
            // League emblems that render as white monochrome on dark (navy
            // brand marks) keep their ORIGINAL dark colors on light.
            property("--l-emblem-filter", "none")
            // Filter tiles (matches filters sheet): white cards on light.
            property("--l-tile", "rgba(255, 255, 255, 0.85)")
            property("--l-tile-border", "rgba(140, 165, 215, 0.50)")
            // Selected interest chip (Focus & Activities): solid blue cell,
            // white glyph + label.
            property("--l-chip-selected", "#0061FF")
            property("--l-chip-selected-text", "#FFFFFF")
            property("--l-chip-selected-icon", "brightness(0) invert(1)")
            // Bio meta rows (address / languages): darker than muted on light.
            property("--l-bio-meta", "#33415E")
            // Dark decorative flourishes (interactions sheet) hidden on light.
            property("--l-deco-opacity", "0")
            // AI-avatar info cards: white on light; softer blue shadow.
            property("--l-avatar-card", "linear-gradient(160deg, #FFFFFF 0%, #F0F5FC 100%)")
            property("--l-shadow-c", "rgba(23, 43, 99, 0.18)")
            // Ambient transparent pitch/stadium art — visible pastel on light,
            // scaled past the viewport so the PNG's transparent side margins
            // don't read as gutters.
            property("--l-ambient-art", "0.7")
            property("--l-ambient-art-scale", "scale(1.25)")
            // Map popup body text (popup surface flips to white on light).
            property("--l-popup-sub", "#5A6B8C")
            property("--l-chip-bg", "rgba(255, 255, 255, 0.60)")
            property("--l-chip-border", "rgba(140, 165, 215, 0.55)")
            property("--l-white-chip-border", "rgba(163, 180, 214, 0.60)")
            property("--l-text-strong", "#101B33")
            property("--l-text-glow", "none")
            property("--l-text-shadow", "none")
            property("--l-story-muted", "#5A6B8C")
        }
    }
    // Header nav buttons: the raw SVG glyphs ship in pale header-blue
    // (#B4CCF6 family) tuned for the dark chrome — darken them to slate on
    // the light theme (news-switcher tone). Badges are plain divs, so
    // counters keep their color.
    ctx.stylesheet.registerStyleBase("[data-theme=\"light\"] .arena-hnb img, [data-theme=\"light\"] .arena-hnb svg") {
        Modifier.styleModifier {
            property("filter", "brightness(0.30) saturate(1.5)")
        }
    }
    // Header chrome (light): pills sit on a slightly darker base than the
    // page — matches the news mode switcher / profile controls. The pills
    // paint "InteractiveSecondary over var(--arena-bg)", so a local
    // --arena-bg override inside the header darkens all of them at once.
    ctx.stylesheet.registerStyleBase("[data-theme=\"light\"] .arena-header") {
        Modifier.styleModifier {
            property("--arena-bg", "#E2EAF6")
        }
    }
    // Mobile top bar glyphs (not wrapped in .arena-hnb): darken /icons/ art
    // and inline SVGs on light. Scoped to icon files so avatar photos and
    // country flags (/flags/) keep their colors.
    ctx.stylesheet.registerStyleBase(
        "[data-theme=\"light\"] .arena-header img[src^=\"/icons/\"]:not([src*=\"logo\"]), " +
            "[data-theme=\"light\"] .arena-header span > svg"
    ) {
        Modifier.styleModifier {
            property("filter", "brightness(0.30) saturate(1.5)")
        }
    }

    // Smooth theme switch: ThemeController adds .theme-transition to <html>
    // for ~250ms around the attribute flip.
    ctx.stylesheet.registerStyleBase("html.theme-transition, html.theme-transition *") {
        Modifier.styleModifier {
            property(
                "transition",
                "background-color 200ms ease, color 200ms ease, " +
                    "border-color 200ms ease, box-shadow 200ms ease, filter 200ms ease"
            )
        }
    }

    ctx.stylesheet.registerStyleBase("body") {
        Modifier
            .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)
            .fontSize(16.px)
            .fontWeight(AppTypography.Regular)
            .margin(0.px)
            .padding(0.px)
            .overflow(Overflow.Hidden)
            .styleModifier {
                // Figma 10567:190904 — two-layer fill (dark): solid #02040B
                // base + 40%-opacity blue tint gradient, with the noise SVG
                // layered on top to prevent banding. Light theme swaps the
                // whole stack for the aurora background. Values live in the
                // --arena-bg* tokens above.
                property("background-color", "var(--arena-bg)")
                property("background-image", "var(--arena-bg-image)")
                property("background-repeat", "var(--arena-bg-repeat)")
            }
    }

    ctx.stylesheet.registerStyleBase("html") {
        Modifier
            .overflow(Overflow.Hidden)
            .styleModifier {
                property("background-color", "var(--arena-bg)")
            }
    }

    // Make Silk's .silk-surface wrapper transparent so only body background shows
    // Must use !important to override inline style set by Silk's Surface composable
    ctx.stylesheet.registerStyleBase(".silk-surface") {
        Modifier.styleModifier {
            property("background-color", "transparent !important")
        }
    }

    ctx.stylesheet.registerStyleBase("input::placeholder, textarea::placeholder") {
        Modifier.styleModifier {
            property("color", "var(--arena-placeholder)")
            property("opacity", "1")
        }
    }

    // App-wide selection / drag suppression so the page reads like a native
    // app — no rogue text highlighting on long-press, no ghost previews when
    // dragging cards or avatars around. Inputs override this back to `text`
    // below so users can still edit form fields normally.
    ctx.stylesheet.registerStyleBase("body") {
        Modifier.styleModifier {
            property("user-select", "none")
            property("-webkit-user-select", "none")
            property("-moz-user-select", "none")
            property("-ms-user-select", "none")
            property("-webkit-touch-callout", "none")
        }
    }
    ctx.stylesheet.registerStyleBase("input, textarea, [contenteditable=\"true\"], [contenteditable=\"\"]") {
        Modifier.styleModifier {
            property("user-select", "text")
            property("-webkit-user-select", "text")
            property("-moz-user-select", "text")
            property("-ms-user-select", "text")
        }
    }
    ctx.stylesheet.registerStyleBase("img") {
        Modifier.styleModifier {
            property("-webkit-user-drag", "none")
            property("user-drag", "none")
            property("pointer-events", "auto")
            property("-webkit-touch-callout", "none")
        }
    }

    // Kill the Android Chrome tap-highlight (the grey/blue "ripple" flash that
    // fires on every tap). iOS/desktop never show it; this makes Android match.
    // Transparent rather than removed so nothing else changes.
    ctx.stylesheet.registerStyleBase("*") {
        Modifier.styleModifier {
            property("-webkit-tap-highlight-color", "transparent")
        }
    }

    // Map container fill — Leaflet defaults to a light (#ddd) backdrop that
    // flashes white on the sides while tiles load or beyond the world bounds
    // when zoomed out. Force the app's dark navy so uncovered areas read dark.
    // outline:none — Leaflet gives the container tabindex=0 (keyboard pan), so
    // after a map click any key press (e.g. ⌥) flips the browser into keyboard
    // modality and paints a :focus-visible ring around the full-bleed map; its
    // top edge shows up as a stray horizontal line under the top bar.
    ctx.stylesheet.registerStyleBase(".leaflet-container") {
        Modifier.styleModifier {
            property("background", "var(--arena-map-bg)")
            property("outline", "none")
        }
    }
    // Same for the MapLibre GL canvas nested inside (vector basemap).
    ctx.stylesheet.registerStyleBase(".maplibregl-canvas") {
        Modifier.styleModifier {
            property("outline", "none")
        }
    }

    // NOTE: the Arena browse map gets its navy look from the vector basemap
    // style itself (MapLibre GL layer in LeafletMap.initReadOnly, mirroring the
    // mobile Google Maps cloud style) — no CSS filter on the tile pane (it
    // would repaint every zoom frame) and no wash overlay (it would mute the
    // styled colors).

    // Leaflet zoom controls — themed to match the app's dark surface.
    // Leaflet renders these inside the map container with its own CSS; we
    // override the relevant classes globally so every map looks consistent.
    ctx.stylesheet.registerStyleBase(".leaflet-bar") {
        Modifier.styleModifier {
            property("background", "var(--l-map-chrome, rgba(11, 22, 40, 0.85))")
            property("backdrop-filter", "blur(8px)")
            property("-webkit-backdrop-filter", "blur(8px)")
            property("border", "0.5px solid rgba(0, 97, 255, 0.35)")
            property("border-radius", "12px")
            property("box-shadow", "0 6px 18px rgba(0, 0, 0, 0.35)")
            property("overflow", "hidden")
        }
    }
    ctx.stylesheet.registerStyleBase(".leaflet-bar a, .leaflet-bar a:link") {
        Modifier.styleModifier {
            property("background-color", "transparent")
            property("color", "var(--arena-text-2)")
            property("border-bottom", "0.5px solid var(--l-hairline, rgba(255, 255, 255, 0.08))")
            property("width", "32px")
            property("height", "32px")
            property("line-height", "32px")
            property("font-size", "20px")
            property("font-weight", "500")
            property("text-decoration", "none")
        }
    }
    ctx.stylesheet.registerStyleBase(".leaflet-bar a:last-child") {
        Modifier.styleModifier {
            property("border-bottom", "none")
        }
    }
    ctx.stylesheet.registerStyleBase(".leaflet-bar a:hover, .leaflet-bar a:focus") {
        Modifier.styleModifier {
            property("background-color", "rgba(0, 97, 255, 0.18)")
            property("color", "var(--l-text-strong, #ffffff)")
        }
    }
    ctx.stylesheet.registerStyleBase(".leaflet-bar a.leaflet-disabled") {
        Modifier.styleModifier {
            property("background-color", "transparent")
            property("color", "var(--l-map-chrome-disabled, rgba(190, 214, 245, 0.3))")
            property("cursor", "default")
        }
    }
    // Hide Leaflet's bottom-right "Leaflet | OpenStreetMap" attribution chip —
    // it clashes with the dark theme and is not needed in our embedded views.
    ctx.stylesheet.registerStyleBase(".leaflet-control-attribution") {
        Modifier.styleModifier {
            property("background", "var(--l-map-chrome, rgba(11, 22, 40, 0.7))")
            property("color", "var(--l-map-attr, rgba(190, 214, 245, 0.6))")
            property("border-radius", "6px 0 0 0")
            property("font-size", "10px")
        }
    }
    ctx.stylesheet.registerStyleBase(".leaflet-control-attribution a") {
        Modifier.styleModifier {
            property("color", "var(--l-map-attr-link, rgba(190, 214, 245, 0.85))")
        }
    }

    // Arena map marker popups — dark mini event cards (LeafletMap.addMarkers).
    // Leaflet's default popup chrome is white; restyle it to the app surface.
    ctx.stylesheet.registerStyleBase(".arena-map-popup .leaflet-popup-content-wrapper") {
        Modifier.styleModifier {
            property("background", "var(--arena-surface-popup)")
            property("color", "var(--l-text-strong, #ffffff)")
            property("border-radius", "16px")
            property("border", "0.5px solid rgba(0, 97, 255, 0.35)")
            property("box-shadow", "0 12px 32px rgba(0, 0, 0, 0.5)")
            property("padding", "0")
            property("overflow", "hidden")
        }
    }
    ctx.stylesheet.registerStyleBase(".arena-map-popup .leaflet-popup-content") {
        Modifier.styleModifier {
            property("margin", "0")
        }
    }
    ctx.stylesheet.registerStyleBase(".arena-map-popup .leaflet-popup-tip") {
        Modifier.styleModifier {
            property("background", "var(--arena-surface-popup)")
            property("border", "0.5px solid rgba(0, 97, 255, 0.35)")
        }
    }
    // Event card strip floating over the Arena map — hide its scrollbar.
    ctx.stylesheet.registerStyleBase("#arena-map-cards::-webkit-scrollbar") {
        Modifier.styleModifier {
            property("display", "none")
        }
    }

    // Arena map info-card pins (LeafletMap.addMarkers divIcon) — a mini event
    // card (photo + date/time + title) floating above a teardrop location pin
    // whose tip marks the venue.
    ctx.stylesheet.registerStyleBase(".arena-pin") {
        Modifier.styleModifier {
            property("background", "none")
            property("border", "none")
        }
    }
    ctx.stylesheet.registerStyleBase(".arena-pin-wrap") {
        Modifier.styleModifier {
            property("position", "absolute")
            property("left", "0")
            property("top", "0")
            // Origin at the tail tip keeps the pin anchored to its venue when
            // the zoom-driven scale (set by LeafletMap.initReadOnly) changes.
            property("transform-origin", "50% 100%")
            property("transform", "translate(-50%, -100%) scale(var(--arena-pin-scale, 1))")
            property("transition", "transform 0.15s ease-out")
            property("display", "flex")
            property("flex-direction", "column")
            property("align-items", "center")
            property("cursor", "pointer")
        }
    }
    ctx.stylesheet.registerStyleBase(".arena-pin-card") {
        Modifier.styleModifier {
            property("display", "flex")
            property("flex-direction", "column")
            property("justify-content", "flex-end")
            property("gap", "1px")
            property("position", "relative")
            property("width", "104px")
            property("height", "58px")
            property("padding", "5px 6px")
            property("box-sizing", "border-box")
            // Photo (when present) layered under the scrim via the inline
            // background-image stack; navy base shows when there is no photo.
            property("background-color", "#0F172A")
            property("background-size", "cover")
            property("background-position", "center")
            property("border-radius", "12px")
            property("box-shadow", "0 8px 20px rgba(0, 0, 0, 0.45)")
            property("overflow", "hidden")
        }
    }
    ctx.stylesheet.registerStyleBase(".arena-pin-datetime") {
        Modifier.styleModifier {
            property("font-size", "9px")
            property("font-weight", "600")
            property("color", "#CFE0FF")
            property("white-space", "nowrap")
            property("overflow", "hidden")
            property("text-overflow", "ellipsis")
            property("text-shadow", "0 1px 2px rgba(0, 0, 0, 0.6)")
        }
    }
    ctx.stylesheet.registerStyleBase(".arena-pin-title") {
        Modifier.styleModifier {
            property("font-size", "11px")
            property("font-weight", "500")
            property("color", "#ffffff")
            property("overflow", "hidden")
            property("text-overflow", "ellipsis")
            property("white-space", "nowrap")
            property("text-shadow", "0 1px 2px rgba(0, 0, 0, 0.6)")
        }
    }
    ctx.stylesheet.registerStyleBase(".arena-pin-point") {
        Modifier.styleModifier {
            // Teardrop location pin below the card (Figma Events Map): 36px
            // icon under a ~91px card scaled to our 58px mini-card.
            property("width", "24px")
            property("height", "24px")
            property("margin-top", "6px")
            property("filter", "drop-shadow(0 4px 8px rgba(0, 0, 0, 0.5))")
        }
    }
    ctx.stylesheet.registerStyleBase(".arena-pin-point svg") {
        Modifier.styleModifier {
            property("display", "block")
            property("width", "100%")
            property("height", "100%")
        }
    }

    // Arena club info-card pins — name on top, "location · sport" meta line,
    // rating badge top-left over the photo (mirrors the mobile club list card).
    ctx.stylesheet.registerStyleBase(".arena-club-pin-name") {
        Modifier.styleModifier {
            property("font-size", "11px")
            property("font-weight", "600")
            property("color", "#ffffff")
            property("overflow", "hidden")
            property("text-overflow", "ellipsis")
            property("white-space", "nowrap")
            property("text-shadow", "0 1px 2px rgba(0, 0, 0, 0.6)")
        }
    }
    ctx.stylesheet.registerStyleBase(".arena-club-pin-meta") {
        Modifier.styleModifier {
            property("font-size", "9px")
            property("font-weight", "500")
            property("color", "#CFE0FF")
            property("overflow", "hidden")
            property("text-overflow", "ellipsis")
            property("white-space", "nowrap")
            property("text-shadow", "0 1px 2px rgba(0, 0, 0, 0.6)")
        }
    }
    ctx.stylesheet.registerStyleBase(".arena-club-pin-rating") {
        Modifier.styleModifier {
            property("position", "absolute")
            property("top", "4px")
            property("left", "4px")
            property("display", "flex")
            property("align-items", "center")
            property("gap", "2px")
            property("padding", "1px 5px")
            property("border-radius", "8px")
            property("background", "rgba(0, 0, 0, 0.5)")
            property("border", "0.3px solid rgba(255, 255, 255, 0.4)")
            property("font-size", "9px")
            property("font-weight", "600")
            property("color", "#ffffff")
            property("line-height", "1.3")
        }
    }
    ctx.stylesheet.registerStyleBase(".arena-club-pin-star") {
        Modifier.styleModifier {
            property("color", "#0061FF")
            property("font-size", "10px")
            property("line-height", "1")
        }
    }

    // Marker cluster disc (leaflet.markercluster iconCreateFunction) — a solid
    // brand-blue circle with the white child count.
    ctx.stylesheet.registerStyleBase(".arena-cluster") {
        Modifier.styleModifier {
            property("background", "none")
            property("border", "none")
        }
    }
    ctx.stylesheet.registerStyleBase(".arena-cluster-disc") {
        Modifier.styleModifier {
            property("display", "flex")
            property("align-items", "center")
            property("justify-content", "center")
            property("border-radius", "50%")
            property("background", "#0061FF")
            property("color", "#ffffff")
            property("font-weight", "600")
            property("font-size", "14px")
            property("border", "2px solid rgba(255, 255, 255, 0.85)")
            property("box-shadow", "0 4px 12px rgba(0, 0, 0, 0.4)")
        }
    }
}
